import { useUserInfo } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { HomeCardSection, type HomeCardSectionItem } from "./HomeCardSection";
import type { HubService } from "./__generated__/HomePageDataQuery.graphql";
import type { HubFilterService as Service } from "./layout/HubMap/hubFilters";

type HomeCardSectionsProps = {
	onSelectHub: (service?: Service, to?: string) => void;
	scannedHub?: {
		name: string;
	} | null;
	scannedHubServices?: readonly HubService[] | null;
	hasManagedCompartmentAccess: boolean;
	hasWarenannahmeAccess: boolean;
};

export const HomeCardSections = (props: HomeCardSectionsProps) => {
	const { onSelectHub, scannedHub, scannedHubServices, hasManagedCompartmentAccess, hasWarenannahmeAccess } = props;
	const i18n = useI18nContext().LL["new-ui"];
	const user = useUserInfo();

	const shopCollectItem: HomeCardSectionItem = {
		id: "discover-shop-collect",
		title: i18n.SHOP_N_COLLECT_TITLE(),
		description: i18n.HOME_SHOP_COLLECT_DESC(),
		imageSrc: "/consumer-home-assets/discover-take-away-card-2.webp",
		to: "/$hubMetaName/shop",
		service: "SHOP_N_COLLECT" as const,
		availabilityService: "CLICK_N_COLLECT" as const,
	};

	const discoverItems: HomeCardSectionItem[] = [
		{
			id: "discover-instant-buy",
			title: i18n.INSTANT_BUY_TITLE(),
			description: i18n.HOME_INSTANT_BUY_DESC(),
			imageSrc: "/consumer-home-assets/discover-take-away-card-1.webp",
			to: "/$hubMetaName/instant-buy",
			service: "INSTANT_BUY",
		},
		shopCollectItem,
	];

	const dropItems: HomeCardSectionItem[] = [
		{
			id: "drop-lock-personal",
			title: i18n.HOME_LOCK_PERSONAL_TITLE(),
			description: i18n.HOME_LOCK_PERSONAL_DESC(),
			imageSrc: "/consumer-home-assets/discover-take-away-card-3.webp",
			to: "/$hubMetaName/lock",
			service: "LOCK_B2C",
		},
		...(user?.organization || hasManagedCompartmentAccess
			? [
					{
						id: "drop-lock-business",
						title: i18n.HOME_LOCK_MANAGED_TITLE(),
						description: i18n.HOME_LOCK_MANAGED_DESC(),
						imageSrc: "/consumer-home-assets/drop-lock.webp",
						to: "/$hubMetaName/swap" as const,
						service: "LOCK_B2B" as const,
					},
				]
			: []),
		...(hasWarenannahmeAccess
			? [
					{
						id: "drop-warenannahme",
						title: i18n.WA_TITLE(),
						description: i18n.WA_DESC(),
						imageSrc: "/consumer-home-assets/drop-warenannahme.webp",
						to: "/$hubMetaName/warenannahme" as const,
						service: "LOCK_B2B" as const,
					},
				]
			: []),
		{
			id: "drop-return",
			title: i18n.HOME_RETURN_ACTION(),
			description: i18n.HOME_RETURN_DESC(),
			imageSrc: "/consumer-home-assets/drop-return.webp",
			badge: i18n.HOME_COMING_SOON(),
			to: "/support",
		},
		{
			id: "drop-send",
			title: i18n.HOME_SEND_ACTION(),
			description: i18n.HOME_SEND_DESC(),
			imageSrc: "/consumer-home-assets/drop-send.webp",
			badge: i18n.HOME_COMING_SOON(),
			to: "/support",
		},
	];

	return (
		<>
			<HomeCardSection
				title={i18n.HOME_DISCOVER_TITLE()}
				subtitle={i18n.HOME_DISCOVER_SUBTITLE()}
				switchToGridLabel={i18n.HOME_SWITCH_TO_GRID()}
				switchToCarouselLabel={i18n.HOME_SWITCH_TO_CAROUSEL()}
				items={discoverItems}
				onSelectHub={onSelectHub}
				scannedHub={scannedHub}
				scannedHubServices={scannedHubServices}
			/>

			<HomeCardSection
				title={i18n.HOME_DROP_TITLE()}
				subtitle={i18n.HOME_DROP_SUBTITLE()}
				switchToGridLabel={i18n.HOME_SWITCH_TO_GRID()}
				switchToCarouselLabel={i18n.HOME_SWITCH_TO_CAROUSEL()}
				items={dropItems}
				onSelectHub={onSelectHub}
				scannedHub={scannedHub}
				scannedHubServices={scannedHubServices}
			/>
		</>
	);
};
